import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { useTheme } from '../../theme';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  BackHandler,
  TextInput,
} from 'react-native';
import { useSelector } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import PTDropDown from './PTDropDown';
import LhTextInput from '../../components/LhTextInput';
import LhButton from '../../components/LhButton';
import { useToast } from '../../components/LhToastContainer';
import LhDatePicker from '../../components/LhDatePicker';
import { EquipmentValue, CreateCertificationRequest, RouteTypeItem } from './types';
import {
  fetchActiveRouteTypes,
  fetchActiveLoadSequences,
  fetchEquipmentConfigItems,
  checkRouteUniqueness,
  createCertification,
  updateCertification,
  fetchPhotoCategories,
  fetchRouteNumbersByLocation,
  fetchDeliveryDetail,
  saveDraft,
} from './perfectTruckApi';
import { EquipmentConfigItem, PhotoCategoryItem } from '../Admin/pages/PerfectTruckManagement/types';
import PTPhotoUpload from './PTPhotoUpload';
import UnsavedChangesDialog from './UnsavedChangesDialog';
import { formatUserName } from './perfectTruckUtils';
import { resolveRouteTypeId } from './perfectTruckUtils';
import type { RootState } from '../../store';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// System-level item: always first on certifier form, not in config table
const TRUCK_PLUGGED_IN_ITEM: EquipmentConfigItem = {
  id: 0,
  equipmentName: 'Truck Plugged In / Jack Charging',
  certifierPosLabel: 'Yes',
  certifierNegLabel: 'No',
  checkerPosLabel: 'Yes',
  checkerNegLabel: 'No',
  showOnCertifierForm: true,
  showOnCheckerForm: false,
  displayOrder: -1,
  isActive: true,
  isDuo: false,
  isFsv: false,
  organizationType: null,
  is3pl: false,
};

// Convert Date -> yyyy-MM-dd for the API
const toApiDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Determine default delivery date based on current local time
// If time is 00:00-03:59, delivery date = today
// If time is 04:00-23:59, delivery date = next day
const getDefaultDeliveryDate = (): Date => {
  const now = new Date();
  const hours = now.getHours();

  if (hours < 4) {
    // Before 4 AM: delivery date is today
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  // 4 AM or later: delivery date is tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
};

// Parse date string (yyyy-MM-dd) as local date, avoiding UTC timezone conversion
// Example: "2026-06-18" creates a Date at 00:00:00 local time on 2026-06-18
const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

const normalizeEquipmentRouteTypes = (routeType: EquipmentConfigItem['routeType']): string[] => {
  if (Array.isArray(routeType)) {
    return routeType
      .map(v => String(v).trim())
      .filter(Boolean);
  }

  if (!routeType) {
    return [];
  }

  return String(routeType)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
};

const normalizePhotoRouteTypes = (routeType: PhotoCategoryItem['routeType']): string[] => {
  if (Array.isArray(routeType)) {
    return routeType.map(v => String(v).trim()).filter(Boolean);
  }

  if (!routeType) return [];

  return String(routeType).split(',').map(v => v.trim()).filter(Boolean);
};

type StoredCertifierResponseValue = 'pos' | 'neg';
type StoredCertifierResponseEntry = StoredCertifierResponseValue | { value: StoredCertifierResponseValue; remarks?: string };

const normalizeResponseKey = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const readStoredCertifierSelection = (
  stored: unknown,
  positiveLabel: string,
  negativeLabel: string,
): { value: EquipmentValue; remarks: string } => {
  let rawValue: unknown = stored;
  let remarks = '';

  if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
    const candidate = stored as { value?: unknown; remarks?: unknown };
    rawValue = candidate.value;
    if (typeof candidate.remarks === 'string') {
      remarks = candidate.remarks;
    }
  }

  const normalized = normalizeResponseKey(rawValue);
  const normalizedPositive = normalizeResponseKey(positiveLabel);
  const normalizedNegative = normalizeResponseKey(negativeLabel);

  if (normalized === 'pos' || normalized === 'yes' || (normalizedPositive && normalized === normalizedPositive)) {
    return { value: 'yes', remarks };
  }

  if (normalized === 'neg' || normalized === 'no' || (normalizedNegative && normalized === normalizedNegative)) {
    return { value: 'no', remarks };
  }

  return { value: null, remarks };
};

const serializeCertifierResponseEntry = (
  item: EquipmentConfigItem | null,
  value: EquipmentValue,
  remarks: string,
): StoredCertifierResponseEntry | null => {
  if (value === 'yes') {
    if (item?.isTextBoxRequired || remarks) {
      return { value: 'pos', remarks };
    }
    return 'pos';
  }

  if (value === 'no') {
    if (item?.isTextBoxRequired || remarks) {
      return { value: 'neg', remarks };
    }
    return 'neg';
  }

  return null;
};

const buildCertifierResponse = (
  items: EquipmentConfigItem[],
  selections: Record<number, EquipmentValue>,
  remarksById: Record<number, string>,
  truckSelection: EquipmentValue,
): string => {
  const response: Record<string, StoredCertifierResponseEntry | null> = {
    [String(TRUCK_PLUGGED_IN_ITEM.id)]: serializeCertifierResponseEntry(null, truckSelection, ''),
  };

  items.forEach(item => {
    const value = selections[item.id] ?? null;
    const remarks = (remarksById[item.id] ?? '').trim();
    response[String(item.id)] = serializeCertifierResponseEntry(item, value, remarks);
  });

  return JSON.stringify(response);
};

const isAllRouteType = (routeTypes: string[]): boolean => {
  if (routeTypes.length === 0) {
    return true;
  }

  return routeTypes.some(rt => rt.toLowerCase() === 'all');
};

const filterEquipmentByRouteType = (
  items: EquipmentConfigItem[],
  selectedRouteType?: string,
  preservedEquipmentNames: string[] = [],
  routeTypeConfig: RouteTypeItem[] = [],
): EquipmentConfigItem[] => {
  // Keep current behavior until a route type is explicitly selected.
  if (!selectedRouteType) {
    return items;
  }

  const selected = resolveRouteTypeId(selectedRouteType, routeTypeConfig).toLowerCase();
  const preservedNamesSet = new Set(preservedEquipmentNames.map(name => name.toLowerCase()));

  return items.filter(item => {
    if (preservedNamesSet.has(item.equipmentName.toLowerCase())) {
      return true;
    }

    const configuredRoutes = normalizeEquipmentRouteTypes(item.routeType);
    if (isAllRouteType(configuredRoutes)) {
      return true;
    }

    return configuredRoutes.some(rt => resolveRouteTypeId(rt, routeTypeConfig).toLowerCase() === selected);
  });
};

const filterPhotoCategoriesByRouteType = (
  items: PhotoCategoryItem[],
  selectedRouteType?: string,
  preservedPhotoLabels: string[] = [],
  routeTypeConfig: RouteTypeItem[] = [],
): PhotoCategoryItem[] => {
  if (!selectedRouteType) return items;

  const selected = resolveRouteTypeId(selectedRouteType, routeTypeConfig).toLowerCase();
  const preservedSet = new Set(preservedPhotoLabels.map(s => s.toLowerCase()));

  return items.filter(item => {
    if (preservedSet.has(item.photoLabel.toLowerCase())) return true;
    const configured = normalizePhotoRouteTypes((item as any).routeType);
    if (isAllRouteType(configured)) return true;
    return configured.some(rt => resolveRouteTypeId(rt, routeTypeConfig).toLowerCase() === selected);
  });
};

// ── Sub-components ────────────────────────────────────────────────────────────

interface CertifierRowProps {
  item: EquipmentConfigItem;
  value: EquipmentValue;
  onChange: (id: number, val: EquipmentValue) => void;
  isMobile?: boolean;
  alt?: boolean;
  disabled?: boolean;
  hasDetails?: boolean;
}

const CertifierRow: React.FC<CertifierRowProps> = ({ item, value, onChange, isMobile, alt, disabled, hasDetails }) => {
  const styles = useDarkStyles(baseStyles);
  return (
    <View style={[
      styles.equipmentRow,
      isMobile && styles.equipmentRowMobile,
      isMobile && alt && styles.equipmentRowMobileAlt,
      hasDetails && { borderBottomWidth: 0 },
    ]}>
      <Text style={styles.equipmentLabel}>{item.equipmentName}</Text>
      <View style={styles.equipmentOpts}>
        <TouchableOpacity
          style={[styles.eqOption, value === 'yes' && styles.eqOptionYes]}
          onPress={() => onChange(item.id, value === 'yes' ? null : 'yes')}
          disabled={disabled}
        >
          <Text style={[styles.eqOptionText, value === 'yes' && styles.eqOptionTextSelected]}>
            {item.certifierPosLabel}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.eqOption, value === 'no' && styles.eqOptionNo]}
          onPress={() => onChange(item.id, value === 'no' ? null : 'no')}
          disabled={disabled}
        >
          <Text style={[styles.eqOptionText, value === 'no' && styles.eqOptionTextSelected]}>
            {item.certifierNegLabel}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export interface PerfectTruckCreateHandle {
  handleNavigationAttempt: (callback: () => void) => void;
}

interface PerfectTruckCreateProps {
  onCancel: () => void;
  onSubmit: () => void;
  draftId?: number; // If provided, load draft data
  readOnly?: boolean; // When true, renders the certification record in view-only mode
}

const PerfectTruckCreate = forwardRef<PerfectTruckCreateHandle, PerfectTruckCreateProps>((props, ref) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { onCancel, onSubmit, draftId, readOnly = false } = props;
  // Certifier from Redux
  const { t, i18n } = useTranslation('perfectTruck');
  const isDarkTheme = useTheme().resolvedTheme === 'dark';
  // Read-only inputs: match the PTDropDown read-only look (same surface/border, faded) instead of a solid grey fill
  const readOnlyInputStyle = {
    backgroundColor: isDarkTheme ? '#000000' : '#FFFFFF',
    borderColor: isDarkTheme ? '#3A3A3A' : '#DEE2E6',
    color: isDarkTheme ? '#B0B0B0' : '#343A40',
    opacity: 0.5,
  };
  const toast = useToast();
  const currentLanguage = (i18n.language ?? 'en').split('-')[0];
  const truckLabelCurrent = t('truckPluggedIn');
  const truckLabelFallbacks = [
    'Truck Plugged In / Jack Charging',
    'Camion branch\u00e9 / Chargeur de cric',
    'Camión Enchufado / Carga de Gato',
  ];
  const allTruckLabels = [truckLabelCurrent, ...truckLabelFallbacks].filter(Boolean).map(s => String(s).trim().toLowerCase());

  const certifierName = useSelector((state: RootState) =>
    state.auth?.user?.name ??
    state.auth?.user?.claims?.name ??
    state.appData?.userInfo?.email ??
    state.auth?.user?.username ??
    ''
  );
  const [recordCertifierName, setRecordCertifierName] = useState<string | null>(null);
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  // Form fields
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [minDeliveryDate, setMinDeliveryDate] = useState<Date | undefined>(undefined);
  const [routeType, setRouteType] = useState<any>(null);
  const [routeNumber, setRouteNumber] = useState('');
  const [loadSequence, setLoadSequence] = useState<any>({ id: 'A', displayText: 'A' });
  const [asset, setAsset] = useState('');
  // imageFileNames maps photoCategoryId (string) -> fileName (for backend API submission)
  // imageUrls maps photoCategoryId (string) -> temporary display URL (SAS token, expires in 60 min)
  const [imageFileNames, setImageFileNames] = useState<Record<string, string>>({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [equipment, setEquipment] = useState<Record<number, EquipmentValue>>({});
  const [equipmentRemarks, setEquipmentRemarks] = useState<Record<number, string>>({});

  // Desktop photo row: measure card content width to compute dynamic box widths
  const [photoCardWidth, setPhotoCardWidth] = useState(0);
  const CARD_PADDING = 40; // 20 left + 20 right
  const PHOTO_GAP = 12;
  const MAX_VISIBLE = 3;

  // Config data from API
  const [routeTypes, setRouteTypes] = useState<RouteTypeItem[]>([]);
  const [loadSequenceOptions, setLoadSequenceOptions] = useState<{ id: string; displayText: string }[]>([]);
  const [allCertifierItems, setAllCertifierItems] = useState<EquipmentConfigItem[]>([]);
  const [preservedCertifierEquipmentNames, setPreservedCertifierEquipmentNames] = useState<string[]>([]);
  const [initialDraftRouteType, setInitialDraftRouteType] = useState<string | null>(null);
  const [certifierItems, setCertifierItems] = useState<EquipmentConfigItem[]>([]);
  const [photoCategories, setPhotoCategories] = useState<PhotoCategoryItem[]>([]);
  const [allPhotoCategories, setAllPhotoCategories] = useState<PhotoCategoryItem[]>([]);
  const [preservedPhotoCategoryLabels, setPreservedPhotoCategoryLabels] = useState<string[]>([]);
  const [routeNumbers, setRouteNumbers] = useState<string[]>([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // System-level equipment state
  const [truckPluggedIn, setTruckPluggedIn] = useState<EquipmentValue>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRouteDuplicate, setIsRouteDuplicate] = useState(false);
  const [assetError, setAssetError] = useState('');
  const [apiError, setApiError] = useState('');

  // Draft & unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [unsavedDialogMode, setUnsavedDialogMode] = useState<'save' | 'missing-fields'>('save');
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [certifierComments, setCertifierComments] = useState('');

  const isMobile = Platform.OS !== 'web';

  // Derive whether current route type is WHD (Warehouse Delivered)
  const isWhdRoute = useMemo(() => {
    if (!routeType?.id) return false;
    return routeTypes.find(rt => String(rt.id) === routeType.id)?.deliveryType === 'WHD';
  }, [routeTypes, routeType?.id]);

  // Load config and draft on mount
  const loadConfig = useCallback(async () => {
    try {
      setIsLoadingConfig(true);

      // Fetch config (all items, active + inactive) and draft in parallel.
      // Inactive items that are still referenced in the delivery response will be
      // included in the rendered form; truly-active items are always included.
      // Load sequences are fetched separately so a failure falls back gracefully.
      const [[rtData, eqData, pcData], draft] = await Promise.all([
        Promise.all([
          fetchActiveRouteTypes(currentLanguage),
          fetchEquipmentConfigItems({ language: currentLanguage }),
          fetchPhotoCategories(currentLanguage),
        ]),
        draftId ? fetchDeliveryDetail(draftId) : Promise.resolve(null),
      ]);

      setRouteTypes(rtData);

      // Route type is persisted as a language-independent ID; resolve legacy label-based
      // records and current-language labels back and forth via these lookup maps.
      const routeTypeById = new Map(rtData.map(rt => [String(rt.id), rt] as const));
      const routeTypeByLabel = new Map(rtData.map(rt => [rt.routeType.trim().toLowerCase(), rt] as const));
      let resolvedRouteTypeId: string | null = null;
      let resolvedRouteTypeLabel: string | null = null;
      if (draft?.routeType) {
        const rawRouteType = String(draft.routeType).trim();
        const matchedRouteType = routeTypeById.get(rawRouteType) ?? routeTypeByLabel.get(rawRouteType.toLowerCase());
        resolvedRouteTypeId = matchedRouteType ? String(matchedRouteType.id) : rawRouteType;
        resolvedRouteTypeLabel = matchedRouteType ? matchedRouteType.routeType : rawRouteType;
      }

      // Fetch load sequences from the dedicated API; fall back to static A/B/C on any failure.
      let lsOptions: { id: string; displayText: string }[] = [];
      try {
        const lsData = await fetchActiveLoadSequences(currentLanguage);
        if (lsData && lsData.length > 0) {
          lsOptions = [...lsData]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(ls => ({ id: ls.loadSequence, displayText: ls.loadSequence }));
        }
      } catch {
        // API failed — lsOptions stays empty, fallback applied below
      }
      if (lsOptions.length === 0) {
        lsOptions = [
          { id: 'A', displayText: 'A' },
          { id: 'B', displayText: 'B' },
          { id: 'C', displayText: 'C' },
        ];
      }
      setLoadSequenceOptions(lsOptions);

      // Set default load sequence to the first active one (if not loading a draft)
      if (!draftId && lsOptions.length > 0) {
        setLoadSequence(lsOptions[0]);
      }

      // Parse delivery response keys before filtering so that inactive config
      // items still referenced in the delivery are retained in the UI.
      let preservedCertifierNames = new Set<string>();
      let imageResponseKeys = new Set<string>();
      const configById = new Map(eqData.map(item => [normalizeResponseKey(item.id), item] as const));
      if (draft?.certifierResponse) {
        try {
          const certMap = JSON.parse(draft.certifierResponse) as Record<string, unknown>;
          const normalizedCertMap = new Map<string, unknown>(
            Object.entries(certMap).map(([k, v]) => [normalizeResponseKey(k), v]),
          );
          preservedCertifierNames = new Set(
            eqData
              .filter(item => normalizedCertMap.has(normalizeResponseKey(item.id)) || normalizedCertMap.has(normalizeResponseKey(item.equipmentName)))
              .map(item => item.equipmentName),
          );
        } catch {}
      }
      if (draft?.certifierImageResponse) {
        try {
          const parsedImgKeys = Object.keys(JSON.parse(draft.certifierImageResponse));
          // Build a lookup map from photo category id -> photoLabel for resolution
          const pcById = new Map((pcData || []).map(p => [String(p.id), p.photoLabel.trim().toLowerCase()] as const));
          imageResponseKeys = new Set(
            parsedImgKeys.flatMap(k => {
              const raw = String(k).trim().toLowerCase();
              // Include both the raw key and, when it is a numeric id, the resolved label
              const resolved = /^\d+$/.test(raw) ? pcById.get(raw) : undefined;
              return resolved ? [raw, resolved] : [raw];
            }),
          );
        } catch {}
      }

      // // SNAPSHOT LOGIC: Use equipment config snapshot if available (for existing drafts)
      // // For new certificates, use only currently-active equipment
      // let certifier: EquipmentConfigItem[];
      // if (draftId && draft?.certifierConfigSnapshot) {
      //   // Use snapshot captured at creation time
      //   try {
      //     const snapshotItems = JSON.parse(draft.certifierConfigSnapshot) as EquipmentConfigItem[];
      //     certifier = snapshotItems.sort((a, b) => a.displayOrder - b.displayOrder);
      //   } catch (e) {
      //     console.warn('Failed to parse certifierConfigSnapshot, using current config');
      //     // Fallback to current config if parsing fails
        // If opened in read-only view, only show items that certifier actually submitted
        const certifier = eqData
          .filter(i => {
            const nameKey = String(i.equipmentName ?? '').trim().toLowerCase();
            if (readOnly) {
              return i.showOnCertifierForm && preservedCertifierNames.has(i.equipmentName);
            }
            return i.showOnCertifierForm && (i.isActive || preservedCertifierNames.has(i.equipmentName));
          })
          .sort((a, b) => a.displayOrder - b.displayOrder);
      //   }
      // } else {
      //   // New certificate: Use only currently-active equipment
      //   certifier = eqData
      //     .filter(i => i.showOnCertifierForm && i.isActive)
      //     .sort((a, b) => a.displayOrder - b.displayOrder);
      // }
      let resolvedCertifierItems = certifier;
      
      const active = (pcData || [])
        .filter(p => p.isActive || imageResponseKeys.has(String(p.id)) || imageResponseKeys.has(p.photoLabel.trim().toLowerCase()))
        .sort((a, b) => a.displayOrder - b.displayOrder);
      setAllPhotoCategories(active);
      let mergedPhotoList = active;


      // Fetch route numbers if location is available
      if (selectedLocation?.name) {
        try {
          const routeNums = await fetchRouteNumbersByLocation(selectedLocation.name);
          setRouteNumbers(routeNums);
        } catch (e: any) {
          setRouteNumbers([]);
        }
      }

      // Map draft fields to form state
      if (draftId && draft) {
        try {
          setRecordCertifierName(draft.certifierName ?? null);
          setInitialDraftRouteType(resolvedRouteTypeId);
          setPreservedCertifierEquipmentNames(Array.from(preservedCertifierNames));

          // Load delivery date from draft using local date parser (no UTC conversion)
          if (draft.deliveryDate) setDeliveryDate(parseLocalDate(draft.deliveryDate));
          if (draft.routeType) setRouteType({ id: resolvedRouteTypeId, displayText: resolvedRouteTypeLabel });
          if (draft.routeNumber) setRouteNumber(draft.routeNumber);
          if (draft.loadSeq) {
            setLoadSequence({ id: draft.loadSeq, displayText: draft.loadSeq });
          } else if (lsOptions.length > 0) {
            setLoadSequence(lsOptions[0]);
          }
          if (draft.assetId) setAsset(draft.assetId);
          if (draft.certifierComments) setCertifierComments(draft.certifierComments);

          // Parse certifierResponse JSON for equipment
          // IMPORTANT: use the local `certifier` variable (freshly loaded), NOT the
          // `certifierItems` state which is still stale (empty) at this point in the
          // async function because setState is asynchronous.
          if (draft.certifierResponse) {
            try {
              const certMap = JSON.parse(draft.certifierResponse) as Record<string, unknown>;
              // Build normalized map for case/whitespace-insensitive lookup
              const normalizedCertMap = new Map<string, unknown>(
                Object.entries(certMap).map(([k, v]) => [String(k).trim().toLowerCase(), v])
              );
              const equipMap: Record<number, EquipmentValue> = {};
              certifier.forEach(item => {
                const stored = normalizedCertMap.get(normalizeResponseKey(item.id)) ?? normalizedCertMap.get(normalizeResponseKey(item.equipmentName));
                const parsed = readStoredCertifierSelection(stored, item.certifierPosLabel, item.certifierNegLabel);
                if (parsed.value) {
                  equipMap[item.id] = parsed.value;
                }
                if (parsed.remarks) {
                  setEquipmentRemarks(prev => ({ ...prev, [item.id]: parsed.remarks }));
                }
              });

              // Fallback: add synthetic items for keys that are truly absent from
              // the DB (hard-deleted). Deactivated items are already included above
              // via the isActive || preservedCertifierNames filter.
              const configEquipNames = new Set(
                [truckLabelCurrent, ...certifier.map(item => item.equipmentName)]
                  .map(n => String(n).trim().toLowerCase())
              );
              const extraEquipItems: EquipmentConfigItem[] = [];
              let syntheticEquipId = -1000;
              Object.entries(certMap).forEach(([name, storedLabel]) => {
                const nameKey = String(name).trim().toLowerCase();
                if (allTruckLabels.includes(nameKey)) return;
                const configItemById = configById.get(nameKey);
                if (configItemById && !configEquipNames.has(String(configItemById.equipmentName).trim().toLowerCase())) {
                  resolvedCertifierItems = [...resolvedCertifierItems, configItemById];
                  configEquipNames.add(String(configItemById.equipmentName).trim().toLowerCase());
                  return;
                }
                // Skip if config already contains it
                if (configEquipNames.has(nameKey)) return;
                if (/^\d+$/.test(nameKey)) return;
                const valueOnly = (storedLabel && typeof storedLabel === 'object' && !Array.isArray(storedLabel))
                  ? (storedLabel as { value?: unknown }).value
                  : storedLabel;
                if (valueOnly == null || valueOnly === '') return;
                const id = syntheticEquipId--;
                extraEquipItems.push({
                  id,
                  equipmentName: name,
                  certifierPosLabel: String(valueOnly),
                  certifierNegLabel: '—',
                  checkerPosLabel: '',
                  checkerNegLabel: '',
                  showOnCertifierForm: true,
                  showOnCheckerForm: false,
                  displayOrder: 9999,
                  isActive: false,
                  isDuo: false,
                  isFsv: false,
                  organizationType: null,
                  is3pl: false,
                });
                equipMap[id] = 'yes';
              });
              if (extraEquipItems.length > 0) {
                resolvedCertifierItems = [...certifier, ...extraEquipItems];
              }

              setEquipment(equipMap);
              // Check for truck plugged in item using normalized map
              let truckStored = normalizedCertMap.get(normalizeResponseKey(TRUCK_PLUGGED_IN_ITEM.id));
              if (!truckStored) {
                for (const lbl of allTruckLabels) {
                  const found = normalizedCertMap.get(lbl);
                  if (found !== undefined) { truckStored = found; break; }
                }
              }
              const truckParsed = readStoredCertifierSelection(truckStored, t('review.label_yes'), t('review.label_no'));
              setTruckPluggedIn(truckParsed.value);
            } catch (e) {
              // Error parsing certifierResponse, skip
            }
          }

              // Parse certifierImageResponse: supports both legacy { [photoLabel]: url }
              // and new { [photoCategoryId]: url } formats. Normalize into maps
              // keyed by photoCategoryId (string) for UI state and submission.
              if (draft.certifierImageResponse) {
                try {
                  const imgMap = JSON.parse(draft.certifierImageResponse) as Record<string, string>;
                  const validEntries = Object.entries(imgMap).filter(([, val]) => typeof val === 'string' && val.trim() !== '');

                  const configById = new Map(active.map(p => [String(p.id), p] as const));
                  const configByLabel = new Map(active.map(p => [p.photoLabel.trim().toLowerCase(), p] as const));

                  const fileNameMap: Record<string, string> = {};
                  const urlMap: Record<string, string> = {};
                  const extraPhotoItems: PhotoCategoryItem[] = [];
                  let syntheticPhotoId = -2000;

                  validEntries.forEach(([rawKey, url]) => {
                    const key = String(rawKey).trim();
                    let targetId: string | null = null;

                    if (/^\d+$/.test(key)) {
                      // Numeric key: treat as category id (even if DB no longer lists it)
                      targetId = key;
                    } else {
                      const byLabel = configByLabel.get(key.toLowerCase());
                      if (byLabel) targetId = String(byLabel.id);
                    }

                    if (!targetId) {
                      // Create synthetic photo category for unknown label
                      const id = syntheticPhotoId--;
                      extraPhotoItems.push({ id, photoLabel: rawKey, displayOrder: 9999, isActive: false });
                      targetId = String(id);
                    }

                    urlMap[targetId] = url;
                    const withoutQuery = url.split('?')[0];
                    fileNameMap[targetId] = decodeURIComponent(withoutQuery.split('/').pop() ?? url);
                  });

                  if (extraPhotoItems.length > 0) {
                    const merged = [...active, ...extraPhotoItems];
                    setAllPhotoCategories(merged);
                    mergedPhotoList = merged;
                  }

                  setImageUrls(urlMap);
                  setImageFileNames(fileNameMap);
                } catch (e) {
                  // Error parsing certifierImageResponse, skip
                }
              }

          setHasUnsavedChanges(false);
        } catch (e: any) {
          // Error loading draft, skip
        }
      } else if (!draftId) {
        setRecordCertifierName(null);
        setInitialDraftRouteType(null);
        setPreservedCertifierEquipmentNames([]);

        // New certification: apply default delivery date logic based on current time.
        const defaultDate = getDefaultDeliveryDate();
        setDeliveryDate(defaultDate);
      }

      // Equipment/photo config route types are ID-based (with legacy label fallback),
      // so filter using the resolved ID.
      const selectedRouteTypeId = resolvedRouteTypeId ?? undefined;
      setAllCertifierItems(resolvedCertifierItems);
      setCertifierItems(
        filterEquipmentByRouteType(
          resolvedCertifierItems,
          selectedRouteTypeId,
          draft?.routeType ? Array.from(preservedCertifierNames) : [],
          rtData,
        ),
      );

      // Apply same route-type filtering to photo categories
      setPreservedPhotoCategoryLabels(draft?.routeType ? Array.from(imageResponseKeys) : []);
      setPhotoCategories(
        filterPhotoCategoriesByRouteType(
          mergedPhotoList,
          selectedRouteTypeId,
          draft?.routeType ? Array.from(imageResponseKeys) : [],
          rtData,
        ),
      );
    } catch (e: any) {
      console.error('Failed to load Perfect Truck config:', e);
      // Show a localized message rather than an untranslated backend/network error string
      setApiError(t('create.error_failed_load_config'));
    } finally {
      setIsLoadingConfig(false);
    }
  }, [selectedLocation?.name, draftId, currentLanguage, readOnly]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    const shouldPreserveSavedRouteEquipment = !!initialDraftRouteType && routeType?.id === initialDraftRouteType;
    setCertifierItems(
      filterEquipmentByRouteType(
        allCertifierItems,
        routeType?.id,
        shouldPreserveSavedRouteEquipment ? preservedCertifierEquipmentNames : [],
        routeTypes,
      ),
    );
    const shouldPreserveSavedPhoto = !!initialDraftRouteType && routeType?.id === initialDraftRouteType;
    setPhotoCategories(
      filterPhotoCategoriesByRouteType(
        allPhotoCategories,
        routeType?.id,
        shouldPreserveSavedPhoto ? preservedPhotoCategoryLabels : [],
        routeTypes,
      ),
    );
  }, [allCertifierItems, initialDraftRouteType, preservedCertifierEquipmentNames, routeType?.id, allPhotoCategories, preservedPhotoCategoryLabels, routeTypes]);

  const routeTypeOptions = routeTypes.map(rt => ({
    id: String(rt.id),
    displayText: rt.routeType,
  }));

  const routeNumberOptions = routeNumbers.map(rn => ({
    id: rn,
    displayText: rn,
  }));

  const displayedCertifierName = readOnly
    ? formatUserName(recordCertifierName ?? certifierName)
    : (certifierName || '—');
  const handlePhotoUploaded = (categoryIdOrLabel: string, data: { fileName: string; imageUrl: string }) => {
    // Parent passes category.photoLabel as label; map label -> category id when possible
    const matched = photoCategories.find(p => p.photoLabel === categoryIdOrLabel) ?? allPhotoCategories.find(p => p.photoLabel === categoryIdOrLabel);
    const key = matched ? String(matched.id) : String(categoryIdOrLabel);
    setImageFileNames(prev => ({ ...prev, [key]: data.fileName }));
    setImageUrls(prev => ({ ...prev, [key]: data.imageUrl }));
    setHasUnsavedChanges(true);
  };

  const handleEquipmentChange = (id: number, val: EquipmentValue) => {
    setEquipment(prev => ({ ...prev, [id]: val }));
    setHasUnsavedChanges(true);
    if (val !== 'yes') {
      setEquipmentRemarks(prev => {
        const c = { ...prev } as Record<number, string>;
        delete c[id];
        return c;
      });
    }
  };
  const handleEquipmentRemarksChange = (id: number, text: string) => {
    setEquipmentRemarks(prev => ({ ...prev, [id]: text }));
    setHasUnsavedChanges(true);
  };

  // Track changes to form fields
  const handleDeliveryDateChange = (date: Date | null) => {
    setDeliveryDate(date);
    setHasUnsavedChanges(true);
    runUniquenessCheck(routeNumber, loadSequence?.id, date);
  };

  const handleRouteTypeChange = (value: any) => {
    setRouteType(value);
    setHasUnsavedChanges(true);
  };

  const handleLoadSequenceChange = (value: any) => {
    setLoadSequence(value);
    setHasUnsavedChanges(true);
    runUniquenessCheck(routeNumber, value?.id, deliveryDate);
  };

  const handleAssetChange = (value: string) => {
    if (value.length > 15) {
      setAssetError(t('create.error_asset_max'));
    } else {
      setAsset(value);
      setAssetError('');
      setHasUnsavedChanges(true);
    }
  };

  const handleCertifierCommentsChange = (value: string) => {
    setCertifierComments(value);
    setHasUnsavedChanges(true);
  };

  const handleTruckPluggedInChange = (value: EquipmentValue) => {
    setTruckPluggedIn(value);
    setHasUnsavedChanges(true);
  };

  // Shared uniqueness check — only fires when all 3 required fields are filled
  const runUniquenessCheck = useCallback(async (
    rNum: string,
    lSeq: string | undefined,
    dDate: Date | null,
  ) => {
    // For WHD routes, don't require load sequence for uniqueness check
    const effectiveLSeq = isWhdRoute ? undefined : lSeq;
    if (!rNum.trim() || (!isWhdRoute && !lSeq) || !dDate) {
      setIsRouteDuplicate(false);
      return;
    }
    const apiDate = toApiDate(dDate);
    try {
      setIsRouteDuplicate(false);
      const result = await checkRouteUniqueness(rNum.trim(), effectiveLSeq, apiDate, selectedLocation?.name);
      setIsRouteDuplicate(!result.unique);
    } catch {
      // Non-blocking
    }
  }, [selectedLocation?.name, isWhdRoute]);

  // Computed (not stored) so the message re-translates automatically when the language changes
  const routeErrorText = isRouteDuplicate
    ? t('create.error_route_duplicate', { route: routeNumber, seq: loadSequence?.id })
    : '';

  // Route number selected from dropdown
  const handleRouteNumberChange = (selectedOption: any) => {
    const val = selectedOption?.id || '';
    setRouteNumber(val);
    setHasUnsavedChanges(true);
    runUniquenessCheck(val, loadSequence?.id, deliveryDate);
  };

  // Route number typed manually (free-text when no routes available)
  const handleRouteNumberTextChange = (text: string) => {
    setRouteNumber(text);
    setHasUnsavedChanges(true);
    runUniquenessCheck(text, loadSequence?.id, deliveryDate);
  };

  // Handle navigation with unsaved changes
  const handleNavigationAttempt = (callback: () => void) => {
    if (hasUnsavedChanges) {
      const hasMinimumFields = !!deliveryDate && !!routeNumber.trim() && !!loadSequence?.id;
      setUnsavedDialogMode(hasMinimumFields ? 'save' : 'missing-fields');
      setPendingNavigation(() => callback);
      setShowUnsavedDialog(true);
    } else {
      callback();
    }
  };

  // Expose navigation guard to parent via ref
  useImperativeHandle(ref, () => ({ handleNavigationAttempt }));

  // Intercept Android hardware back button and browser tab close
  useEffect(() => {
    if (Platform.OS === 'android') {
      const subscription = BackHandler.addEventListener('hardwareBackPress', () => {
        handleNavigationAttempt(onCancel);
        return true; // always consume — either shows dialog or navigates to dashboard
      });
      return () => subscription.remove();
    }
    if (Platform.OS === 'web') {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, onCancel]);

  // Save draft
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      setApiError('');

      const draftPayload: Partial<CreateCertificationRequest> & { id?: number } = {
        ...(draftId && { id: draftId }),
        status: 'In Progress - Certifier',
        ...(deliveryDate && { deliveryDate: toApiDate(deliveryDate) }),
        ...(routeType && { routeType: routeType.id }),
        ...(routeNumber && { routeNumber: routeNumber.trim() }),
        ...(loadSequence && !isWhdRoute && { loadSeq: loadSequence.id }),
        ...(asset && { assetId: asset.trim() }),
        ...(selectedLocation && { locationId: selectedLocation.id, locationName: selectedLocation.name }),
        ...(Object.keys(equipment).length > 0 || truckPluggedIn ? {
          certifierResponse: buildCertifierResponse(certifierItems, equipment, equipmentRemarks, truckPluggedIn),
        } : {}),
        ...(Object.keys(imageFileNames).length > 0 && {
          // imageFileNames is now keyed by photoCategoryId (string)
          certifierImageResponse: JSON.stringify(imageFileNames),
        }),
        ...(certifierComments && { certifierComments: certifierComments.trim() }),
        // Capture snapshot only for NEW drafts (not existing ones)
        // ...(!draftId && {
        //   certifierConfigSnapshot: JSON.stringify(
        //     [TRUCK_PLUGGED_IN_ITEM, ...certifierItems].map(item => ({
        //       id: item.id,
        //       equipmentName: item.equipmentName,
        //       certifierPosLabel: item.certifierPosLabel,
        //       certifierNegLabel: item.certifierNegLabel,
        //       showOnCertifierForm: item.showOnCertifierForm,
        //       isActive: item.isActive,
        //       displayOrder: item.displayOrder,
        //     }))
        //   ),
        // }),
      };

      await saveDraft(draftPayload);
      setHasUnsavedChanges(false);
      setShowUnsavedDialog(false);
      toast.showToast({ header: t('create.toast_draft_header'), subtext: t('create.toast_draft_subtext'), variant: 'success' });

      // Execute pending navigation
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    } catch (e: any) {
      console.error('Failed to save Perfect Truck draft:', e);
      setApiError(t('create.error_failed_save_draft'));
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    setApiError('');

    if (!deliveryDate) {
      setApiError(t('create.error_delivery_date_required'));
      return;
    }
    if (!routeType) {
      setApiError(t('create.error_route_type_required'));
      return;
    }
    if (!routeNumber.trim()) {
      setApiError(t('create.error_route_number_required'));
      return;
    }
    if (!isWhdRoute && !asset.trim()) {
      setApiError(t('create.error_asset_required'));
      return;
    }
    if (!isWhdRoute && !loadSequence?.id) {
      setApiError(t('create.error_load_sequence_required'));
      return;
    }
    if (!selectedLocation?.id || !selectedLocation?.name) {
      setApiError(t('create.error_no_location'));
      return;
    }
    const apiDate = toApiDate(deliveryDate);
    if (isRouteDuplicate) {
      setApiError(routeErrorText);
      return;
    }

    // All equipment items must be answered
    if (truckPluggedIn === null) {
      setApiError(t('create.error_truck_plugged_in'));
      return;
    }
    const unanswered = certifierItems.filter(item => !equipment[item.id]);
    if (unanswered.length > 0) {
      setApiError(t('create.error_select_equipment', { items: unanswered.map(i => i.equipmentName).join(', ') }));
      return;
    }

    // If any equipment requires a text box and is answered positive, ensure remarks provided
    const missingRemarks = certifierItems.filter(item => item.isTextBoxRequired && equipment[item.id] === 'yes' && !(equipmentRemarks[item.id] && equipmentRemarks[item.id].trim()));
    if (missingRemarks.length > 0) {
      setApiError(t('create.error_provide_details', { items: missingRemarks.map(i => i.equipmentName).join(', ') }));
      return;
    }

      // All photo categories must be uploaded (imageFileNames now keyed by category id)
    const missingPhotos = photoCategories.filter(p => !imageFileNames[String(p.id)]);
    if (missingPhotos.length > 0) {
      setApiError(t('create.error_upload_photos', { items: missingPhotos.map(p => p.photoLabel).join(', ') }));
      return;
    }

    const certifierResponse = buildCertifierResponse(certifierItems, equipment, equipmentRemarks, truckPluggedIn);

    const payload: CreateCertificationRequest = {
      routeNumber: routeNumber.trim(),
      routeType: routeType.id,
      ...(loadSequence && !isWhdRoute && { loadSeq: loadSequence.id || undefined }),
      ...(asset && { assetId: asset.trim() }),
      deliveryDate: apiDate,
      locationId: selectedLocation.id,
      locationName: selectedLocation.name,
      certifierResponse,
      certifierImageResponse: Object.keys(imageFileNames).length > 0
        ? JSON.stringify(imageFileNames)
        : undefined,
      ...(certifierComments && { certifierComments: certifierComments.trim() }),
      // // Capture equipment configuration snapshot for future reference
      // certifierConfigSnapshot: JSON.stringify(
      //   [TRUCK_PLUGGED_IN_ITEM, ...certifierItems].map(item => ({
      //     id: item.id,
      //     equipmentName: item.equipmentName,
      //     certifierPosLabel: item.certifierPosLabel,
      //     certifierNegLabel: item.certifierNegLabel,
      //     showOnCertifierForm: item.showOnCertifierForm,
      //     isActive: item.isActive,
      //     displayOrder: item.displayOrder,
      //   }))
      // ),
      status: isWhdRoute ? 'Approved' : 'Pending Check',
    };

    try {
      setApiError('');
      setIsSubmitting(true);
      if (draftId) {
        await updateCertification(draftId, payload);
      } else {
        await createCertification(payload);
      }
      toast.showToast({ header: t('create.toast_submit_header'), subtext: t('create.toast_submit_subtext'), variant: 'success' });
      onSubmit();
    } catch (e: any) {
      console.error('Failed to submit Perfect Truck certification:', e);
      setApiError(t('create.error_submission_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingConfig) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color={dc(AdminColors.blue700)} />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={[styles.contentContainer, isMobile && styles.contentContainerMobile]}>
      {readOnly ? (
        <View style={styles.readOnlyBanner}>
          <MaterialIcons name="visibility" size={16} color={dc(AdminColors.blue700)} />
          <Text style={styles.readOnlyBannerText}>{t('create.banner_read_only')}</Text>
        </View>
      ) : null}
      {/* Route Information */}
      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>{t('create.section_route_info')}</Text>
        </View>
        <View style={[styles.formGrid, isMobile && styles.formGridMobile, { marginTop: 16 }]}>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.req}>* </Text>
              {isWhdRoute ? t('create.label_expected_delivery_date') : t('create.label_delivery_date')}
            </Text>
            <LhDatePicker
              value={deliveryDate}
              onChange={handleDeliveryDateChange}
              placeholder={t('create.placeholder_select_date')}
              minimumDate={minDeliveryDate}
              disabled={readOnly}
              colorScheme={isDarkTheme ? 'dark' : 'light'}
              iconColor={isDarkTheme ? '#FFFFFF' : '#3A3A3A'}
            />
          </View>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>{t('create.label_route_type')}</Text>
            <PTDropDown
              childList={routeTypeOptions}
              selectedValue={routeType}
              setSelectedValue={handleRouteTypeChange}
              placeholder={t('create.placeholder_select')}
              label=""
              readOnly={readOnly}
            />
          </View>
          <View style={[styles.field, isMobile && styles.fieldMobile]}>
            <Text style={styles.fieldLabel}>
              <Text style={styles.req}>* </Text>
              {isWhdRoute ? t('create.label_bol_number') : t('create.label_route_number')}
            </Text>
            {isWhdRoute ? (
              <LhTextInput
                value={routeNumber}
                onChangeText={handleRouteNumberTextChange}
                placeholder={t('create.placeholder_bol_number')}
                editable={!readOnly}
              />
            ) : routeNumbers.length > 0 ? (
              <PTDropDown
                childList={routeNumberOptions}
                selectedValue={routeNumber ? { id: routeNumber, displayText: routeNumber } : null}
                setSelectedValue={handleRouteNumberChange}
                placeholder={t('create.placeholder_select')}
                label=""
                readOnly={readOnly}
              />
            ) : (
              <>
                <LhTextInput
                  value={routeNumber}
                  onChangeText={handleRouteNumberTextChange}
                  placeholder={t('create.placeholder_route_number')}
                  editable={!readOnly}
                  disabledStyle={readOnlyInputStyle}
                />
                <Text style={styles.noRoutesNote}>{t('create.note_no_routes')}</Text>
              </>
            )}
            {routeErrorText ? <Text style={styles.fieldError}>{routeErrorText}</Text> : null}
          </View>
          {!isWhdRoute && (
            <>
              {isMobile ? (
                <View style={styles.twoColRow}>
                  <View style={[styles.field, styles.fieldTwoCol]}>
                    <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>{t('create.label_load_sequence')}</Text>
                    <PTDropDown
                      childList={loadSequenceOptions}
                      selectedValue={loadSequence}
                      setSelectedValue={handleLoadSequenceChange}
                      placeholder={t('create.placeholder_select')}
                      label=""
                      readOnly={readOnly}
                    />
                  </View>
                  <View style={[styles.field, styles.fieldTwoCol]}>
                    <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>{t('create.label_asset')}</Text>
                    <LhTextInput
                      value={asset}
                      onChangeText={handleAssetChange}
                      placeholder={t('create.placeholder_asset')}
                      editable={!readOnly}
                      disabledStyle={readOnlyInputStyle}
                    />
                    {assetError ? <Text style={styles.fieldError}>{assetError}</Text> : null}
                  </View>
                </View>
              ) : (
                <>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>{t('create.label_load_sequence')}</Text>
                    <PTDropDown
                      childList={loadSequenceOptions}
                      selectedValue={loadSequence}
                      setSelectedValue={handleLoadSequenceChange}
                      placeholder={t('create.placeholder_select')}
                      label=""
                      readOnly={readOnly}
                    />
                  </View>
                  <View style={styles.field}>
                    <Text style={styles.fieldLabel}><Text style={styles.req}>* </Text>{t('create.label_asset')}</Text>
                    <LhTextInput
                      value={asset}
                      onChangeText={handleAssetChange}
                      placeholder={t('create.placeholder_asset')}
                      editable={!readOnly}
                      disabledStyle={readOnlyInputStyle}
                    />
                    {assetError ? <Text style={styles.fieldError}>{assetError}</Text> : null}
                  </View>
                </>
              )}
            </>
          )}
          {isWhdRoute && (
            <View style={[styles.field, isMobile && styles.fieldMobile]}>
              <Text style={styles.fieldLabel}>{t('create.label_asset')} <Text style={styles.optionalLabel}>{t('create.label_optional')}</Text></Text>
              <LhTextInput
                value={asset}
                onChangeText={handleAssetChange}
                placeholder={t('create.placeholder_asset')}
                editable={!readOnly}
              />
            </View>
          )}
        </View>
        <Text style={styles.secLabel}>{t('create.label_certifier')}</Text>
        <View style={styles.certifierInfo}>
          <View style={styles.certifierAvatar}>
            <MaterialIcons name="person" size={18} color={AdminColors.textOnDark} />
          </View>
          <Text style={styles.certifierName}>{displayedCertifierName}</Text>
        </View>
      </View>

      {/* Equipment Photos */}
      <View style={[styles.card, isMobile && styles.cardMobile]} onLayout={e => setPhotoCardWidth(e.nativeEvent.layout.width)}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>{t('create.section_equipment_photos')}</Text>
        </View>
        <Text style={styles.noteText}>
          {isMobile
            ? t('create.note_photo_mobile')
            : photoCategories.length > MAX_VISIBLE
              ? t('create.note_photo_web_scroll')
              : t('create.note_photo_web')}
        </Text>
        {isMobile ? (
          <View style={styles.photoRowMobile}>
            {photoCategories.length > 0 ? (
              photoCategories.map((category) => {
                const innerWidth = photoCardWidth - CARD_PADDING;
                const PHOTO_COLS = 3;
                const PHOTO_GAP_MOBILE = 8;
                const totalGaps = (PHOTO_COLS - 1) * PHOTO_GAP_MOBILE;
                const mobileBoxWidth = innerWidth > 0
                  ? Math.floor((innerWidth - totalGaps) / PHOTO_COLS)
                  : undefined;
                return (
                  <PTPhotoUpload
                    key={category.id}
                    label={category.photoLabel}
                    aspectRatio={1}
                    boxWidth={mobileBoxWidth}
                    initialImageUrl={imageUrls[String(category.id)]}
                    onUploaded={(data) => handlePhotoUploaded(category.photoLabel, data)}
                    disabled={readOnly}
                  />
                );
              })
            ) : (
              <Text style={styles.noteText}>{t('create.note_no_photo_categories')}</Text>
            )}
          </View>
        ) : (
          // Desktop: single unified flex row.
          // ≤ MAX_VISIBLE items → each box uses flex:1, fills the card naturally.
          // > MAX_VISIBLE items → each box gets an explicit width:33.33% (flexGrow/Shrink:0)
          //   so photo content cannot expand the box; items overflow → CSS overflow:scroll scrolls.
          <View
            style={[
              styles.photoRowDesktop,
              photoCategories.length > MAX_VISIBLE && Platform.OS === 'web' && { display: 'flex' as any, flexWrap: 'wrap' as any },
            ]}
          >
            {photoCategories.length > 0 ? (
              photoCategories.map((category) => (
                <View
                  key={`${category.id}-photo-wrapper`}
                  style={
                    photoCategories.length > MAX_VISIBLE
                      ? { width: `30%` as any, flexGrow: 0, flexShrink: 0 }
                      : { flex: 1 }
                  }
                >
                  <PTPhotoUpload
                    key={`${category.id}-${imageFileNames[String(category.id)] ?? 'empty'}`}
                    label={category.photoLabel}
                    aspectRatio={16 / 10}
                    initialImageUrl={imageUrls[String(category.id)]}
                    onUploaded={(data) => handlePhotoUploaded(category.photoLabel, data)}
                    disabled={readOnly}
                  />
                </View>
              ))
            ) : (
              <Text style={styles.noteText}>{t('create.note_no_photo_categories')}</Text>
            )}
          </View>
        )}
      </View>

      {/* Truck & Equipment Status */}
      <View style={[styles.card, isMobile && styles.cardMobile]}>
        <View style={styles.formTitle}>
          <Text style={styles.formTitleText}>{t('create.section_equipment_status')}</Text>
        </View>
        <View style={{ marginTop: 12 }}>
          {/* System-level item — always first, not in config table */}
          <View style={[styles.systemRow, isMobile && styles.systemRowMobile]}>
            <View style={styles.systemRowLabel}>
              <Text style={styles.equipmentLabel}>{truckLabelCurrent}</Text>
            </View>
            <View style={styles.equipmentOpts}>
              <TouchableOpacity
                style={[styles.eqOption, truckPluggedIn === 'yes' && styles.eqOptionYes]}
                onPress={() => handleTruckPluggedInChange(truckPluggedIn === 'yes' ? null : 'yes')}
                disabled={readOnly}
              >
                <Text style={[styles.eqOptionText, truckPluggedIn === 'yes' && styles.eqOptionTextSelected]}>
                  {t('review.label_yes')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.eqOption, truckPluggedIn === 'no' && styles.eqOptionNo]}
                onPress={() => handleTruckPluggedInChange(truckPluggedIn === 'no' ? null : 'no')}
                disabled={readOnly}
              >
                <Text style={[styles.eqOptionText, truckPluggedIn === 'no' && styles.eqOptionTextSelected]}>
                  {t('review.label_no')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          {certifierItems.map((item, idx) => (
            <View key={item.id}>
              <CertifierRow
                item={item}
                value={equipment[item.id] ?? null}
                onChange={handleEquipmentChange}
                isMobile={isMobile}
                alt={idx % 2 === 1}
                disabled={readOnly}
                hasDetails={item.isTextBoxRequired && equipment[item.id] === 'yes'}
              />
              {(() => {
                const answeredYes = equipment[item.id] === 'yes';
                const hasRemarks = (equipmentRemarks[item.id] ?? '').toString().trim() !== '';
                const shouldShowDetails = item.isTextBoxRequired && answeredYes && (!readOnly || hasRemarks);
                return shouldShowDetails ? (
                  <View style={styles.commentsFieldInline}>
                    <Text style={styles.detailsLabel}>{t('create.label_additional_details')}</Text>
                    <LhTextInput
                      value={equipmentRemarks[item.id] ?? ''}
                      onChangeText={text => handleEquipmentRemarksChange(item.id, text)}
                      placeholder={t('create.placeholder_additional_details')}
                      editable={!readOnly}
                      disabledStyle={readOnlyInputStyle}
                      multiline
                      numberOfLines={3}
                      style={styles.multilineInput}
                    />
                  </View>
                ) : null;
              })()}
            </View>
          ))}
          {certifierItems.length === 0 && (
            <Text style={styles.noteText}>{t('create.note_no_equipment')}</Text>
          )}
          <View style={styles.commentsField}>
            <Text style={styles.fieldLabel}>{t('create.label_certifier_comments')}</Text>
            <TextInput
              style={styles.commentsInput}
              multiline
              numberOfLines={3}
              placeholder={t('create.placeholder_certifier_comments')}
              placeholderTextColor={dc(AdminColors.textMuted)}
                value={certifierComments}
                onChangeText={handleCertifierCommentsChange}
                editable={!readOnly}
            />
          </View>
        </View>
      </View>

      {/* Actions */}
      {apiError ? (
        <View style={[styles.validationError, isDarkTheme && { backgroundColor: '#9B2626' }]}>
          <MaterialIcons name="error-outline" size={16} color={isDarkTheme ? '#FFFFFF' : AdminColors.red400} />
          <Text style={styles.validationErrorText}>{apiError}</Text>
        </View>
      ) : null}
      <View style={[styles.formActions, isMobile && styles.formActionsMobile]}>
        {readOnly ? (
          <LhButton
            text={t('create.btn_close')}
            variant="secondary"
            size={isMobile ? 'small' : 'medium'}
            style={isMobile ? styles.actionBtnMobile : undefined}
            textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
            onPress={onCancel}
          />
        ) : (
          <>
            <LhButton
              text={t('create.btn_cancel')}
              variant="secondary"
              size={isMobile ? 'small' : 'medium'}
              style={isMobile ? styles.actionBtnMobile : undefined}
              textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
              onPress={() => handleNavigationAttempt(onCancel)}
            />
            <LhButton
              text={isSubmitting ? t('create.btn_submitting') : t('create.btn_submit')}
              variant="primary"
              size={isMobile ? 'small' : 'medium'}
              style={isMobile ? styles.actionBtnMobile : undefined}
              textStyle={isMobile ? styles.actionBtnTextMobile : undefined}
              onPress={handleSubmit}
              disabled={isSubmitting}
            />
          </>
        )}
      </View>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        visible={showUnsavedDialog}
        mode={unsavedDialogMode}
        onSaveForLater={handleSaveDraft}
        onContinueEditing={() => {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }}
        onCloseWithoutSaving={() => {
          setShowUnsavedDialog(false);
          setHasUnsavedChanges(false);
          if (pendingNavigation) {
            pendingNavigation();
            setPendingNavigation(null);
          }
        }}
        isLoading={isSavingDraft}
      />
    </ScrollView>
  );
});

const baseStyles = StyleSheet.create({
  container: { flex: 1, backgroundColor: AdminColors.bgPage },
  contentContainer: { padding: 20 },
  contentContainerMobile: { padding: 14, paddingBottom: 32 },
  loadingWrapper: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 48 },
  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: AdminColors.red75, borderRadius: 8, padding: 12, marginBottom: 12,
  },
  errorText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.red400, flex: 1 },
  readOnlyBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: AdminColors.blue25, borderRadius: 8, padding: 12, marginBottom: 16,
  },
  readOnlyBannerText: { fontFamily: FontFamily.medium, fontSize: 13, color: AdminColors.blue700, flex: 1 },
  card: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: { elevation: 2 },
    }),
  },
  cardMobile: {
    backgroundColor: 'transparent',
    borderRadius: 0,
    padding: 0,
    marginBottom: 20,
    ...Platform.select({
      web: { boxShadow: 'none' } as any,
      default: { elevation: 0 },
    }),
  },
  formTitle: {
    borderBottomWidth: 2,
    borderBottomColor: AdminColors.borderTable,
    paddingBottom: 8,
    marginBottom: 16,
  },
  formTitleText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    color: AdminColors.blue700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  secLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: AdminColors.textMuted,
    marginBottom: 6,
    marginTop: 14,
  },
  certifierInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    backgroundColor: AdminColors.blue25,
    borderRadius: 8,
  },
  certifierAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: AdminColors.blue700,
    alignItems: 'center',
    justifyContent: 'center',
  },
  certifierName: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.textPrimary,
  },
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 14,
  },
  formGridMobile: { flexDirection: 'column', flexWrap: 'nowrap', gap: 14, alignItems: 'stretch' },
  field: { flex: 1, minWidth: 160, flexDirection: 'column', gap: 4 },
  fieldMobile: { flex: 0, flexBasis: 'auto', width: '100%', alignSelf: 'stretch', minWidth: 0 },
  twoColRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  fieldTwoCol: { flex: 1, flexBasis: 0, width: 'auto', alignSelf: 'auto', minWidth: 0 },
  fieldLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.textPrimary,
    marginBottom: 4,
  },
  optionalLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
  },
  commentsFieldInline: {
    marginTop: 0,
    marginBottom: 8,
  },
  detailsLabel: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.textMuted,
    marginBottom: 6,
  },
  multilineInput: {
    minHeight: 64,
    textAlignVertical: 'top',
  },
  fieldError: {
    fontFamily: FontFamily.medium,
    fontSize: 11,
    color: AdminColors.red400,
    marginTop: 2,
  },
  noRoutesNote: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
    marginTop: 4,
  },
  req: { color: AdminColors.red400 },
  noteText: {
    fontFamily: FontFamily.regular,
    fontSize: 11,
    color: AdminColors.textMuted,
    fontStyle: 'italic',
    marginTop: 8,
    marginBottom: 10,
  },
  photoRowDesktop: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
    paddingBottom: 4,
  },
  photoRowMobile: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  photoBox: {
    flex: 1,
    minWidth: '30%',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: AdminColors.borderDefault,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: AdminColors.blue25,
  },
  photoBoxFilled: {
    borderStyle: 'solid',
    borderColor: AdminColors.green600,
    backgroundColor: AdminColors.green75,
  },
  photoLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 12,
    color: AdminColors.textMuted,
    textAlign: 'center',
  },
  photoLabelFilled: { color: AdminColors.green600 },
  equipmentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  equipmentRowMobile: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderBottomWidth: 1.5,
    borderBottomColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
    marginBottom: 6,
  },
  equipmentRowMobileAlt: {
    backgroundColor: AdminColors.blue25,
  },
  equipmentLabel: { fontFamily: FontFamily.bold, fontSize: 13, color: AdminColors.textPrimary, flex: 1 },
  equipmentOpts: { flexDirection: 'row', gap: 6 },
  eqOption: {
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  eqOptionYes: { backgroundColor: AdminColors.green600, borderColor: AdminColors.green600 },
  eqOptionNo: { backgroundColor: AdminColors.red400, borderColor: AdminColors.red400 },
  eqOptionText: { fontFamily: FontFamily.bold, fontSize: 12, color: AdminColors.textSecondary },
  eqOptionTextSelected: { color: AdminColors.textOnDark },
  systemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.gray50,
  },
  systemRowMobile: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: AdminColors.borderTable,
    borderBottomWidth: 1.5,
    borderBottomColor: AdminColors.borderTable,
    backgroundColor: AdminColors.blue25,
    marginBottom: 6,
  },
  systemRowLabel: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  systemBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: AdminColors.blue25,
    borderWidth: 1,
    borderColor: AdminColors.blue50,
  },
  systemBadgeText: {
    fontFamily: FontFamily.bold,
    fontSize: 9,
    color: AdminColors.blue700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  formActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    paddingTop: 8,
    marginBottom: 20,
  },
  formActionsMobile: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 8,
  },
  actionBtnMobile: {
    flex: 1,
    height: 44,
    borderRadius: 22,
    paddingHorizontal: 6,
    justifyContent: 'center',
  },
  actionBtnTextMobile: {
    fontSize: 13,
  },
  validationError: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: AdminColors.red75,
    borderLeftWidth: 3,
    borderLeftColor: AdminColors.red400,
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  validationErrorText: {
    flex: 1,
    fontFamily: FontFamily.medium,
    fontSize: 13,
    color: AdminColors.red400,
    lineHeight: 18,
  },
  commentsField: { marginTop: 14 },
  commentsInput: {
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    borderRadius: 8,
    padding: 12,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    minHeight: 80,
    textAlignVertical: 'top',
  },
});

export default PerfectTruckCreate;